
import {
  defineComponent,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  PropType,
  Ref,
  ref,
  watch
} from "vue";
import { Carousel, Navigation, Pagination, Slide } from "vue3-carousel";
import "vue3-carousel/dist/carousel.css";
import {
  checkDailyDecoinLimit,
  dispatchErrorBox,
  enableBodyOverflow
} from "../mixins";
import { RepositoryException } from "../../../../domain/exceptions/exceptions";
import { useStore } from "vuex";
import { Pill } from "../../../../domain/entities/pill";
import { useRouter } from "vue-router";
import { dispatchError } from "../../utils/error_handling";

declare const window: any;

export default defineComponent({
  components: {
    Carousel,
    Slide,
    Navigation,
    Pagination
  },
  props: {
    pills: {
      type: Array as PropType<Pill[]>,
      required: true
    },
    startPill: {
      type: [Number, String],
      required: true
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    /** That is because the Carousel Component does not have a specified type. */
    const carouselElement = ref(null) as any;

    /** Index of the current pill in the pills array. */
    const currentPillIndex: Ref<number> = ref(0);

    const router = useRouter();

    /** Carousel layout breakpoints. */
    const breakpoints = ref({
      0: {
        itemsToShow: 1.5
      },

      640: {
        itemsToShow: 1.6
      },

      700: {
        itemsToShow: 2.5
      },

      900: {
        itemsToShow: 2.5
      },

      1024: {
        itemsToShow: 2.8
      }
    });

    /** Tells whether the onBeforeMount hook has already been called or not. */
    const isDOMMounted = ref(false);

    document.addEventListener("keydown", function(e) {
      if (e.key === "Escape") {
        emit("close");
      }
    });

    onBeforeMount(async () => {
      currentPillIndex.value = props.startPill as number;
      await requestIframeApi();
      await loadVideo(props.pills[currentPillIndex.value].url);
      await addStyleToIframe();
      // seeMoreDescription()
      isDOMMounted.value = true;
    });

    onMounted(() => {
      carouselElement.value.restartCarousel();
      carouselElement.value.data.currentSlide.value = currentPillIndex.value;
    });

    watch(
      () => currentPillIndex.value,
      async () => {
        if (isDOMMounted.value) {
          const iframe = document.getElementById("iframe") as HTMLIFrameElement;

          if (iframe) {
            await removeIframe(iframe);
          }

          await loadVideo(props.pills[currentPillIndex.value].url);
          await addStyleToIframe();
        }
      }
    );

    onBeforeUnmount(async () => {
      try {
        await store.dispatch("pill_module/list", {
          index: 0
        });

        const iframe = document.getElementById("iframe") as HTMLIFrameElement;
        if (iframe) {
          iframe.remove();
        }

        const youtubeIframeApiScript = document.getElementById("scriptAPI");
        if (youtubeIframeApiScript) {
          youtubeIframeApiScript.remove();
        }
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    });

    const getCodeYoutube = (url: string): string => {
      const parts = url.split("/");
      const code = parts.pop();
      if (code) {
        return code;
      } else return "";
    };

    /** Makes a request to the YouTube Iframe API. */
    const requestIframeApi = async (): Promise<void | Error> => {
      try {
        const tag = document.createElement("script");
        tag.id = "scriptAPI";
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[1];
        if (firstScriptTag) {
          if (firstScriptTag.parentNode)
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }
      } catch (e) {
        console.error(e);
        await dispatchErrorBox("Ocorreu um erro inesperado.");
      }
    };

    window.onYouTubeIframeAPIReady = async (): Promise<void> => {
      await loadVideo(props.pills[currentPillIndex.value].url);
      await addStyleToIframe();
    };

    const onPlayerReady = async (event): Promise<void> => {
      event.target.playVideo();
      await addStyleToIframe();
      if (!props.pills[currentPillIndex.value].isWatched) {
        await watchPill(props.pills[currentPillIndex.value].id);
        await checkDailyDecoinLimit();
      }
    };

    /** The YouTube Iframe API will call this function when the player's state changes. */
    const onPlayerStateChange = (event): void => {
      if (event.data === 0) {
        let nextIndex = currentPillIndex.value + 1;

        if (nextIndex >= props.pills.length) {
          nextIndex = 0;
        }

        slideTo(nextIndex);
        event.target.playVideo();
      }
    };

    /** Loads a video by ID.
     * @param url url of the video.
     */
    const loadVideo = async (url: string): Promise<void> => {
      try {
        window.player = new window.YT.Player("iframe", {
          videoId: getCodeYoutube(url),
          host: "https://www.youtube.com",
          playerVars: {
            autoplay: 1,
            controls: 0,
            modestbranding: 1,
            rel: 0
          },
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange
          }
        });
      } catch (e) {
        console.error(e);
      }
    };

    /** Adds style to the iframe that runs the current pill. This is done
     * dynamicaly with inline style because of the limitations established by
     * the YouTube Iframe Api. */
    const addStyleToIframe = async (): Promise<void> => {
      const iframe = document.getElementById("iframe") as HTMLIFrameElement;
      iframe.style.height = "100%";
      iframe.style.width = "100%";
      iframe.style.position = "absolute";
      iframe.style.left = "0";
      iframe.style.zIndex = "1";
      iframe.style.borderRadius = "10px";
    };

    /** Removes an iframe from the DOM. */
    const removeIframe = async (iframe: HTMLIFrameElement): Promise<void> => {
      iframe.remove();
    };

    /** Makes a request to set the state of the pill as watched. */
    const watchPill = async (pillID: number): Promise<void> => {
      try {
        await store.dispatch("pill_module/watch", pillID);
        props.pills[currentPillIndex.value].isWatched = true;
      } catch (e) {
        await dispatchError(e);
      }
    };

    const slideTo = (index: number): void => {
      carouselElement.value.slideTo(index);
      currentPillIndex.value = index;
    };

    const prev = (): void => {
      carouselElement.value.prev();
      currentPillIndex.value = carouselElement.value.data.currentSlide.value;
    };

    const next = (): void => {
      carouselElement.value.next();
      currentPillIndex.value = carouselElement.value.data.currentSlide.value;
    };

    const goToCourse = async (id: number) => {
      enableBodyOverflow();
      await router.push({ name: "course", params: { course_id: id } });
    };

    return {
      breakpoints,
      carouselElement,
      currentPillIndex,
      getCodeYoutube,
      slideTo,
      prev,
      next,
      goToCourse
    };
  }
});
